import React, { HTMLAttributes, ReactNode } from "react";
import { mgFlagsCountries, TMGFlagCountryKey } from "../../utils/mgFlagsCountries";
import { MGTag, TMGTagProps } from "../MGTag";
import styles from "./MGOutputItem.module.css";

export const MGOutputItemType = {
  text: "text",
  "long-text": "long-text",
  date: "date",
  time: "time",
} as const;

export const MGOutputItemBackground = {
  light: "light",
  dark: "dark",
} as const;

export type TMGOutputItemType = keyof typeof MGOutputItemType;
export type TMGOutputItemBackground = keyof typeof MGOutputItemBackground;

export type TMGOutputItemImage = {
  alt: string;
  src: string;
};

export type TMGOutputItemAvatar = {
  alt?: string;
  imageSrc?: string;
  username: string;
};

export type TMGOutputItemIcon = {
  ariaLabel?: string;
  element: ReactNode;
  onClick?: () => void;
};

export type TMGOutputItemProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  additionalText?: ReactNode;
  avatarInfo?: TMGOutputItemAvatar;
  background?: TMGOutputItemBackground;
  bankCard?: TMGOutputItemImage;
  dataTestId?: string;
  flag?: TMGFlagCountryKey;
  formatDate?: (text: string) => string;
  formatTime?: (text: string) => string;
  icon?: TMGOutputItemIcon;
  isAdditionalTextLoading?: boolean;
  isDescriptionLoading?: boolean;
  locale?: string;
  logoBank?: TMGOutputItemImage;
  tagInfo?: Omit<TMGTagProps, "size">;
  text: string;
  title: string;
  type?: TMGOutputItemType;
};

const getInitials = (username: string) =>
  username
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();

const formatValue = (
  text: string,
  type: TMGOutputItemType,
  locale: string,
  formatDate?: (text: string) => string,
  formatTime?: (text: string) => string,
) => {
  if (type === "text" || type === "long-text") return text;
  if (type === "date" && formatDate) return formatDate(text);
  if (type === "time" && formatTime) return formatTime(text);

  const value = new Date(text);
  if (Number.isNaN(value.getTime())) return text;

  return type === "date"
    ? new Intl.DateTimeFormat(locale).format(value)
    : new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }).format(value);
};

export const MGOutputItem = ({
  additionalText,
  avatarInfo,
  background = "light",
  bankCard,
  className,
  dataTestId,
  flag,
  formatDate,
  formatTime,
  icon,
  isAdditionalTextLoading = false,
  isDescriptionLoading = false,
  locale = "pt-PT",
  logoBank,
  tagInfo,
  text,
  title,
  type = "text",
  ...htmlProps
}: TMGOutputItemProps) => {
  const flagDefinition = flag ? mgFlagsCountries[flag] : undefined;
  const supportingImage = bankCard || logoBank;
  const formattedValue = formatValue(text, type, locale, formatDate, formatTime);

  return (
    <div
      {...htmlProps}
      className={`${styles.root}${className ? ` ${className}` : ""}`}
      data-background={background}
      data-testid={dataTestId}>
      <div className={styles.title}>{title}</div>

      <div className={styles.content}>
        {avatarInfo && (
          <span className={styles.avatar} aria-hidden="true">
            {avatarInfo.imageSrc ? (
              <img src={avatarInfo.imageSrc} alt={avatarInfo.alt || ""} />
            ) : (
              getInitials(avatarInfo.username)
            )}
          </span>
        )}

        <div className={styles.textWrapper}>
          <div className={styles.textLine}>
            {isDescriptionLoading ? (
              <span
                className={`${styles.skeleton} ${styles.descriptionSkeleton}`}
                data-testid={dataTestId && `${dataTestId}-description-loading`}
                aria-hidden="true"
              />
            ) : (
              <div className={styles.text} data-type={type}>
                {formattedValue}
              </div>
            )}

            {supportingImage && (
              <img
                src={supportingImage.src}
                alt={supportingImage.alt}
                className={bankCard ? styles.bankCard : styles.bankLogo}
              />
            )}

            {icon &&
              (icon.onClick ? (
                <button
                  type="button"
                  className={styles.iconButton}
                  aria-label={icon.ariaLabel || `Ação para ${title}`}
                  onClick={icon.onClick}>
                  {icon.element}
                </button>
              ) : (
                <span className={styles.icon} aria-hidden="true">
                  {icon.element}
                </span>
              ))}
          </div>

          {(additionalText !== undefined || flagDefinition) && (
            <div className={styles.textLine}>
              {additionalText !== undefined && (
                <div className={styles.additionalText}>
                  {isAdditionalTextLoading ? (
                    <span
                      className={`${styles.skeleton} ${styles.additionalTextSkeleton}`}
                      data-testid={dataTestId && `${dataTestId}-additional-loading`}
                      aria-hidden="true"
                    />
                  ) : (
                    additionalText
                  )}
                </div>
              )}

              {flagDefinition && (
                <img
                  src={flagDefinition.flag}
                  alt={flagDefinition.country}
                  className={styles.flag}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {tagInfo && (
        <div className={styles.tag}>
          <MGTag {...tagInfo} size="xs" />
        </div>
      )}

      {(isDescriptionLoading || isAdditionalTextLoading) && (
        <span className={styles.loadingAnnouncement} role="status">
          A carregar
        </span>
      )}
    </div>
  );
};
